import { useInView } from '../../hooks/useInView'
import { GOALS, reachGoal } from '../../lib/metrika'
import patternSvg from '../../assets/why-pattern.svg'

interface BentoCard {
  title: string
  tags: string[]
  description: string
}

const CARDS: BentoCard[] = [
  {
    title: 'Без сложных настроек',
    tags: ['☁️ Облачное решение', '🔒 Безопасно'],
    description:
      'Вам не понадобятся программисты. Не нужно давать доступ к вашему сайту или CRM — просто добавьте описание акции в кабинете.',
  },
  {
    title: 'Воспринимается как подарок',
    tags: ['🎯 Новые лиды', '✨ Высокая мотивация'],
    description:
      'Ваше предложение выглядит не как назойливый баннер, а как приятный и заслуженный бонус за покупку у партнёра.',
  },
  {
    title: 'Быстрый старт',
    tags: ['⚡ Простая регистрация', '💰 Бюджет от 10 000'],
    description:
      'Всего пара шагов от регистрации до запуска кампании. Вы экономите время на согласованиях и долгих брифах.',
  },
  {
    title: 'Горячая аудитория',
    tags: ['📥 Все лиды в кабинете', '🚫 Отсутствие спама'],
    description:
      'Мы показываем вашу акцию людям прямо в момент совершения заказа, когда они максимально лояльны и рады бонусами.',
  },
  {
    title: 'Точный выбор городов',
    tags: ['🗺️ Любые города и регионы'],
    description:
      'Показывайте рекламу только в тех регионах, где работает ваш бизнес. Не тратьте бюджет на нецелевые города.',
  },
  {
    title: 'Проверенные контакты',
    tags: ['📱 Валидация телефонов', '📧 Email коммуникация'],
    description:
      'Чтобы забрать предложение, клиент сам оставляет настоящий телефон или email. Никакого спама и фейковых лидов.',
  },
]

const ROWS: [BentoCard, BentoCard][] = [
  [CARDS[0], CARDS[1]],
  [CARDS[2], CARDS[3]],
  [CARDS[4], CARDS[5]],
]

function Card({ card }: { card: BentoCard }) {
  // Естественный поток: разделитель всегда идёт сразу под пилюлями (pb секции),
  // поэтому отступ под пилюлями одинаков независимо от числа рядов (1 или 2) и ширины.
  // На мобилке высота карточки по контенту (нет «дырок»); от sm — min-h + h-full:
  // жёсткая высота на планшете резала текст, а равенство в ряду даёт стрейч грида.
  return (
    <div className="relative flex min-h-[260px] w-full flex-col overflow-hidden rounded-[20px] border border-[#e4e4e7] bg-gradient-to-b from-white to-[#f4f4f5] sm:h-full sm:min-h-[354px]">
      {/* Декоративный паттерн */}
      <img
        src={patternSvg}
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full object-fill opacity-50"
      />

      {/* Верхняя часть (градиент): заголовок + теги */}
      <div className="relative flex grow flex-col px-6 pt-[34px] pb-4 sm:pt-[58px]">
        <p className="max-w-[250px] text-[24px] font-medium leading-[30px] tracking-[-0.4px] text-[#27272a] sm:text-[30px] sm:leading-[36px]">
          {card.title}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          {card.tags.map((tag) => {
            const spaceIdx = tag.indexOf(' ')
            const icon = tag.slice(0, spaceIdx)
            const label = tag.slice(spaceIdx + 1)
            return (
              <div
                key={tag}
                className="group flex-none flex items-center gap-1 rounded-[8px] bg-white px-3 py-[6px]"
                style={{ boxShadow: '0px 0px 0px 2px rgba(0,0,0,0.1)' }}
              >
                <span className="inline-block text-[13px] leading-[22px] group-hover:animate-wiggle">{icon}</span>
                <span className="whitespace-nowrap bg-gradient-to-t from-[#3f3f46] to-black bg-clip-text text-[13px] leading-[22px] text-transparent">
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Подпись снизу */}
      <div className="relative border-t border-[#e4e4e7] bg-white px-4 pb-[20px] pt-[14px] sm:pb-[35px] sm:pt-[17px]">
        <p className="font-body text-[15px] leading-[22px] text-[#52525b] sm:text-[16px] sm:leading-6">
          {card.description}
        </p>
      </div>
    </div>
  )
}

function slideStyle(inView: boolean, fromLeft: boolean, delay: number) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateX(0)' : `translateX(${fromLeft ? '-60px' : '60px'})`,
    transition: `opacity 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms, transform 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
}

export function Why() {
  const { ref, inView } = useInView(0.8, '0px 0px -80px 0px')
  return (
    <section
      id="benefits"
      className="mx-auto flex max-w-[1920px] flex-col items-start gap-[26px] px-4 py-14 sm:px-6 sm:py-20 lg:px-5 lg:py-28"
    >
      {/* Заголовок */}
      <div
        ref={ref}
        className="w-full text-center"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s cubic-bezier(0.45,0,0.15,1) 0ms, transform 0.6s cubic-bezier(0.45,0,0.15,1) 0ms',
        }}
      >
        <h2 className="font-display text-[28px] max-[359px]:text-[24px] font-semibold leading-[1.14] text-[#041527] sm:text-[44px] lg:text-[56px] lg:leading-[64px]">
          Почему с{' '}
          <a
            href="https://ris.promo/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => reachGoal(GOALS.showcaseClick, { place: 'секция преимуществ' })}
            className="text-accent underline decoration-from-font"
          >
            RIS.Promo
          </a>{' '}
          вы получаете
          <br className="hidden sm:block" />
          <span className="sm:hidden"> </span>
          реальных клиентов
        </h2>
        <p className="font-body mt-5 text-[15px] leading-[23px] text-[#676b94] sm:text-[16px] sm:leading-[26px] lg:text-[20px] lg:leading-[30px]">
          Аудитория с&nbsp;высокой покупательской готовностью и&nbsp;прозрачным контролем каждого рубля.
        </p>
      </div>

      {/* 3 ряда по 2 карточки — второй ряд прижат вправо (стагер) */}
      <div className="flex w-full flex-col gap-5">
        {ROWS.map(([left, right], i) => (
          <div
            key={i}
            className={
              i === 1
                ? 'grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:max-w-[940px] sm:ml-auto'
                : 'grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:max-w-[940px] sm:mr-auto'
            }
          >
            <div style={slideStyle(inView, true, 150 + i * 200)}>
              <Card card={left} />
            </div>
            <div style={slideStyle(inView, false, 250 + i * 200)}>
              <Card card={right} />
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
